import React, { memo, useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { sql } from "@codemirror/lang-sql";
import { EditorView, placeholder as codeMirrorPlaceholder } from "@codemirror/view";

const SQL_EDITOR_THEME = EditorView.theme({
  "&": {
    fontSize: "14px"
  },
  ".cm-editor": {
    border: "1px solid rgb(226 232 240)",
    borderRadius: "0.75rem",
    backgroundColor: "#ffffff"
  },
  ".cm-editor.cm-focused": {
    outline: "none",
    borderColor: "#0f172a",
    boxShadow: "0 0 0 2px rgb(15 23 42 / 0.1)"
  },
  ".cm-scroller": {
    minHeight: "6rem",
    fontFamily:
      "SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace"
  },
  ".cm-content, .cm-line": {
    padding: "0",
    fontFamily:
      "SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, Courier New, monospace"
  },
  ".cm-content": {
    padding: "0.75rem 0.875rem"
  },
  ".cm-placeholder": {
    color: "rgb(148 163 184)",
    fontStyle: "normal"
  },
  ".cm-gutters": {
    display: "none"
  },
  "&.cm-disabled .cm-editor": {
    backgroundColor: "rgb(248 250 252)"
  },
  "&.cm-disabled .cm-content": {
    color: "rgb(100 116 139)"
  }
});

export type SqlAutocompleteContext = {
  sourceEntityName?: string;
  targetEntityName?: string;
  sourceColumns?: string[];
  targetColumns?: string[];
};

function SqlEditorFieldComponent({
  label,
  value,
  onChange,
  placeholder,
  autocomplete,
  height = "96px",
  readOnly = false,
  disabled = false
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autocomplete?: SqlAutocompleteContext;
  height?: string;
  readOnly?: boolean;
  disabled?: boolean;
}): JSX.Element {
  const extensions = useMemo(() => {
    const sourceEntityName = autocomplete?.sourceEntityName?.trim();
    const targetEntityName = autocomplete?.targetEntityName?.trim();
    const sourceColumns =
      autocomplete?.sourceColumns?.filter((value): value is string => Boolean(value?.trim())) ?? [];
    const targetColumns =
      autocomplete?.targetColumns?.filter((value): value is string => Boolean(value?.trim())) ?? [];

    const schema: Record<string, string[]> = {};

    if (sourceEntityName && sourceColumns.length) {
      schema[sourceEntityName] = sourceColumns;
      schema.src = sourceColumns;
    }

    if (targetEntityName && targetColumns.length) {
      schema[targetEntityName] = targetColumns;
      schema.tgt = targetColumns;
    }

    return [
      sql({
        upperCaseKeywords: true,
        schema: Object.keys(schema).length ? schema : undefined,
        defaultTable: sourceEntityName || targetEntityName || undefined
      }),
      EditorView.lineWrapping,
      codeMirrorPlaceholder(placeholder ?? ""),
      SQL_EDITOR_THEME
    ];
  }, [autocomplete, placeholder]);
  const labelMutedClass = readOnly && !disabled ? "tw-text-slate-400" : "tw-text-slate-500";

  return (
    <div className="tw-group tw-flex tw-flex-col tw-gap-1.5 tw-border-b tw-border-slate-100 tw-pb-4">
      <label
        className={`tw-text-[0.6rem] tw-font-semibold tw-uppercase tw-tracking-[0.3em] ${labelMutedClass}`}
      >
        {label}
      </label>
      <div className={disabled ? "cm-disabled" : undefined}>
        <CodeMirror
          value={value}
          height={height}
          extensions={extensions}
          editable={!readOnly && !disabled}
          onChange={onChange}
          basicSetup={{
            lineNumbers: false,
            foldGutter: false,
            dropCursor: false,
            allowMultipleSelections: false,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            highlightActiveLine: false,
            highlightActiveLineGutter: false,
            searchKeymap: false
          }}
        />
      </div>
    </div>
  );
}

export const SqlEditorField = memo(
  SqlEditorFieldComponent,
  (previous, next) =>
    previous.label === next.label &&
    previous.value === next.value &&
    previous.placeholder === next.placeholder &&
    previous.height === next.height &&
    previous.readOnly === next.readOnly &&
    previous.disabled === next.disabled &&
    previous.autocomplete?.sourceEntityName === next.autocomplete?.sourceEntityName &&
    previous.autocomplete?.targetEntityName === next.autocomplete?.targetEntityName &&
    previous.autocomplete?.sourceColumns === next.autocomplete?.sourceColumns &&
    previous.autocomplete?.targetColumns === next.autocomplete?.targetColumns
);
